import { useCurrentUser, useLogout, useSwitchOrg } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import {
	AlertTriangle,
	Box,
	Check,
	ChevronDown,
	GitBranch,
	LayoutDashboard,
	LogOut,
	Route,
	Settings,
	Shield,
	Users,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';

const NAV_ITEMS = [
	{ path: '/', label: 'Dashboard', icon: LayoutDashboard },
	{ path: '/incidents', label: 'Incidents', icon: AlertTriangle },
	{ path: '/policies', label: 'Policies', icon: Shield },
	{ path: '/posture', label: 'Posture', icon: GitBranch },
	{ path: '/attack-paths', label: 'Attack Paths', icon: Route },
] as const;

const ADMIN_NAV = { path: '/admin', label: 'Platform Admin', icon: Users } as const;
const INVENTORY_NAV = {
	label: 'Inventory',
	icon: Box,
	children: [
		{ path: '/inventory/access', label: 'Access' },
		{ path: '/inventory/actions', label: 'Actions' },
	],
} as const;

export function Layout() {
	const { data: user } = useCurrentUser();
	const logout = useLogout();
	const switchOrg = useSwitchOrg();
	const location = useLocation();
	const [orgMenuOpen, setOrgMenuOpen] = useState(false);
	const [inventoryOpen, setInventoryOpen] = useState(() =>
		location.pathname.startsWith('/inventory'),
	);
	const orgMenuRef = useRef<HTMLDivElement>(null);

	const hasMultipleOrgs = (user?.availableOrgs?.length ?? 0) > 1;
	const inventoryExpanded = inventoryOpen || location.pathname.startsWith('/inventory');

	return (
		<div className="flex h-screen bg-background">
			{/* Sidebar */}
			<aside className="flex w-64 flex-col border-r border-border bg-card">
				{/* Logo */}
				<div className="flex h-14 items-center border-b border-border px-4">
					<Link to="/" className="flex items-center gap-2 font-semibold text-foreground">
						<Shield className="h-5 w-5 text-primary" />
						<span>Uncain</span>
					</Link>
				</div>

				{/* Navigation */}
				<nav className="flex-1 space-y-1 p-3">
					{NAV_ITEMS.map((item) => {
						const isActive =
							item.path === '/'
								? location.pathname === '/'
								: location.pathname.startsWith(item.path);
						return (
							<Link
								key={item.path}
								to={item.path}
								className={cn(
									'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
									isActive
										? 'bg-primary/10 text-primary'
										: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
								)}
							>
								<item.icon className="h-4 w-4" />
								{item.label}
							</Link>
						);
					})}

					<div className="space-y-1">
						<button
							type="button"
							onClick={() => setInventoryOpen((value) => !value)}
							aria-expanded={inventoryExpanded}
							className={cn(
								'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
								location.pathname.startsWith('/inventory')
									? 'bg-primary/10 text-primary'
									: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
							)}
						>
							<INVENTORY_NAV.icon className="h-4 w-4" />
							<span className="flex-1 text-left">{INVENTORY_NAV.label}</span>
							<ChevronDown
								className={cn('h-4 w-4 transition-transform', inventoryExpanded && 'rotate-180')}
							/>
						</button>

						{inventoryExpanded && (
							<div className="space-y-1 pl-6">
								{INVENTORY_NAV.children.map((item) => (
									<Link
										key={item.path}
										to={item.path}
										className={cn(
											'block rounded-md px-3 py-2 text-sm transition-colors',
											location.pathname.startsWith(item.path)
												? 'bg-primary/10 font-medium text-primary'
												: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
										)}
									>
										{item.label}
									</Link>
								))}
							</div>
						)}
					</div>

					<Link
						to="/settings"
						className={cn(
							'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
							location.pathname.startsWith('/settings')
								? 'bg-primary/10 text-primary'
								: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
						)}
					>
						<Settings className="h-4 w-4" />
						Settings
					</Link>

					{/* Admin nav — only for platform admins */}
					{user?.platformRole === 'platform_admin' && (
						<Link
							to={ADMIN_NAV.path}
							className={cn(
								'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
								location.pathname.startsWith(ADMIN_NAV.path)
									? 'bg-primary/10 text-primary'
									: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
							)}
						>
							<ADMIN_NAV.icon className="h-4 w-4" />
							{ADMIN_NAV.label}
						</Link>
					)}
				</nav>

				{/* Org switcher + User section */}
				{user && (
					<div className="border-t border-border p-3">
						{/* Org switcher */}
						<div className="relative" ref={orgMenuRef}>
							<button
								type="button"
								onClick={() => hasMultipleOrgs && setOrgMenuOpen(!orgMenuOpen)}
								onBlur={(e) => {
									if (!orgMenuRef.current?.contains(e.relatedTarget)) {
										setOrgMenuOpen(false);
									}
								}}
								className={cn(
									'flex w-full items-center gap-3 rounded-md px-3 py-2',
									hasMultipleOrgs ? 'cursor-pointer hover:bg-accent' : 'cursor-default',
								)}
							>
								{user.currentOrg?.orgAvatarUrl ? (
									<img
										src={user.currentOrg.orgAvatarUrl}
										alt={user.currentOrg.orgLogin}
										className="h-6 w-6 rounded-md"
									/>
								) : (
									<div className="flex h-6 w-6 items-center justify-center rounded-md bg-muted text-xs font-medium text-muted-foreground">
										{(user.currentOrg?.orgLogin ?? '?')[0]?.toUpperCase()}
									</div>
								)}
								<span className="flex-1 truncate text-left text-sm font-medium text-foreground">
									{user.currentOrg?.orgLogin ?? 'No org'}
								</span>
								{hasMultipleOrgs && (
									<ChevronDown
										className={cn(
											'h-4 w-4 text-muted-foreground transition-transform',
											orgMenuOpen && 'rotate-180',
										)}
									/>
								)}
							</button>

							{/* Org dropdown */}
							{orgMenuOpen && hasMultipleOrgs && (
								<div className="absolute bottom-full left-0 mb-1 w-full rounded-md border border-border bg-popover py-1 shadow-md">
									{user.availableOrgs.map((org) => (
										<button
											key={org.orgId}
											type="button"
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => {
												if (org.orgId !== user.currentOrg?.orgId) {
													switchOrg.mutate(org.orgId);
												}
												setOrgMenuOpen(false);
											}}
											className="flex w-full items-center gap-3 px-3 py-2 text-sm hover:bg-accent"
										>
											{org.orgAvatarUrl ? (
												<img
													src={org.orgAvatarUrl}
													alt={org.orgLogin}
													className="h-5 w-5 rounded-md"
												/>
											) : (
												<div className="flex h-5 w-5 items-center justify-center rounded-md bg-muted text-xs font-medium text-muted-foreground">
													{org.orgLogin[0]?.toUpperCase()}
												</div>
											)}
											<span className="flex-1 truncate text-left text-foreground">
												{org.orgLogin}
											</span>
											{org.orgId === user.currentOrg?.orgId && (
												<Check className="h-4 w-4 text-primary" />
											)}
										</button>
									))}
								</div>
							)}
						</div>

						{/* User info + logout */}
						<div className="mt-2 flex items-center gap-3 px-3 py-2">
							{user.avatarUrl && (
								<img src={user.avatarUrl} alt={user.login} className="h-8 w-8 rounded-full" />
							)}
							<div className="flex-1 truncate">
								<div className="truncate text-sm font-medium text-foreground">
									{user.name ?? user.login}
								</div>
								<div className="truncate text-xs text-muted-foreground">
									{user.currentOrg?.role ?? 'member'}
								</div>
							</div>
							<button
								type="button"
								onClick={() => logout.mutate()}
								className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
								title="Sign out"
							>
								<LogOut className="h-4 w-4" />
							</button>
						</div>
					</div>
				)}
			</aside>

			{/* Main content */}
			<main className="flex-1 overflow-auto">
				<div className="mx-auto max-w-7xl p-6">
					<Outlet />
				</div>
			</main>
		</div>
	);
}
